// The yellow frequency tape across the top, after heatmap.py's create_labels.
//
// Tick lines follow heatmap.py exactly: the base interval is the largest power
// of ten that puts at least four ticks across the range, then a ladder of
// finer subdivisions is drawn with taller marks for coarser steps until the
// ticks would crowd closer than 10 px. Labels diverge on purpose: heatmap.py
// only labels ticks more than 50 px apart, which at its native width works
// but at a browser width leaves the tape with no numbers at all. Labels here
// pick the coarsest multiple of the base that keeps text at least 60 px apart,
// formatted the way heatmap.py formats them.

import { colToX, hzToCol, type Clip, type View } from './layout.ts'

export const TAPE_HEIGHT = 25
const TAPE_FONT_PX = 10

const LADDER: readonly [scale: number, y: number][] = [
  [1, 10],
  [5, 15],
  [10, 19],
  [50, 22],
  [100, 24],
  [500, 25],
]

/** The largest power of ten giving at least four ticks in [minHz, maxHz]. */
export function labelBase(minHz: number, maxHz: number): number {
  for (let i = 9; i > 0; i--) {
    const interval = 10 ** i
    const low = Math.floor(minHz / interval) * interval
    const high = (1 + Math.floor(maxHz / interval)) * interval
    const hits = Math.ceil((high - low) / interval)
    if (hits >= 4) return interval
  }
  return 10
}

/** heatmap.py's tape_text label rules. */
export function tapeLabel(hz: number, interval: number): string {
  if (interval >= 1e6) return `${Math.trunc(hz / 1e6)}M`
  if (interval > 1000) {
    const k = `${Math.trunc((hz / 1e3) % 1000)}k`
    return k.startsWith('0') ? `${Math.trunc(hz / 1e6)}M` : k
  }
  let s = `${Math.trunc(hz % 1000)}`
  if (s.startsWith('0')) s = `${Math.trunc((hz / 1e3) % 1000)}k`
  if (s.startsWith('0')) s = `${Math.trunc(hz / 1e6)}M`
  return s
}

export interface TapeGeometry {
  minHz: number
  maxHz: number
  base: number
  /** Tick lines: x in CSS px, top y in CSS px. */
  lines: { x: number; y: number }[]
  /** Labels: x in CSS px, text. */
  labels: { x: number; text: string }[]
}

export function layoutTape(clip: Clip, view: View, widthPx: number): TapeGeometry {
  const minHz = hzAtCol(clip, view.col0)
  const maxHz = hzAtCol(clip, view.col1)
  const base = labelBase(minHz, maxHz)
  const xOf = (hz: number) => colToX(view, hzToCol(clip, hz), widthPx)

  const lines: { x: number; y: number }[] = []
  const used = new Set<number>()
  for (const [scale, y] of LADDER) {
    const interval = base / scale
    const low = Math.floor(minHz / interval) * interval
    const high = (1 + Math.floor(maxHz / interval)) * interval
    let hits = 0
    for (let hz = low; hz < high; hz += interval) {
      if (!(minHz < hz && hz < maxHz)) continue
      hits++
      const key = Math.round(hz)
      if (used.has(key)) continue
      used.add(key)
      lines.push({ x: xOf(hz), y })
    }
    if (hits === 0) continue
    if (widthPx / hits < 10) break
  }

  const labels: { x: number; text: string }[] = []
  const span = maxHz - minHz
  let interval = base
  for (const m of [1, 2, 5, 10, 20, 50, 100, 200, 500, 1000]) {
    interval = base * m
    if ((interval / span) * widthPx >= 60) break
  }
  const low = Math.ceil(minHz / interval) * interval
  for (let hz = low; hz <= maxHz; hz += interval) labels.push({ x: xOf(hz), text: tapeLabel(hz, interval) })

  return { minHz, maxHz, base, lines, labels }
}

function hzAtCol(clip: Clip, col: number): number {
  // colToHz interpolates bin centers; the view edges sit half a bin outside.
  const { binHz, geometry } = clip.run
  const n = binHz.length
  if (col <= 0) return binHz[0]! + col * geometry.stepHz + clip.offsetHz
  if (col >= n - 1) return binHz[n - 1]! + (col - (n - 1)) * geometry.stepHz + clip.offsetHz
  const i = Math.floor(col)
  return binHz[i]! + (col - i) * (binHz[i + 1]! - binHz[i]!) + clip.offsetHz
}

/** Draws the tape into a context whose CSS size is widthPx × TAPE_HEIGHT at `scale` device px per CSS px. */
export function drawTape(ctx: CanvasRenderingContext2D, geometry: TapeGeometry, widthPx: number, scale: number): void {
  ctx.save()
  ctx.scale(scale, scale)
  ctx.fillStyle = '#ffff00'
  ctx.fillRect(0, 0, widthPx, TAPE_HEIGHT)
  ctx.fillStyle = '#000'
  for (const line of geometry.lines) {
    ctx.fillRect(Math.round(line.x), line.y, 1, TAPE_HEIGHT - line.y)
  }
  ctx.font = `${TAPE_FONT_PX}px "Bitstream Vera Sans", "DejaVu Sans", Verdana, sans-serif`
  ctx.textBaseline = 'top'
  for (const label of geometry.labels) {
    const w = ctx.measureText(label.text).width
    let x = label.x - w / 2
    if (x < 1) x = 1
    if (x + w > widthPx - 1) x = widthPx - 1 - w
    ctx.fillText(label.text, x, 0)
  }
  ctx.restore()
}
